import { useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, Share, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  activateWaitingServiceWorker,
  isIosDevice,
  isStandaloneMode,
  PWA_UPDATE_EVENT,
} from "@/lib/pwa";
import { cn } from "@/lib/utils";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

export function PwaControls({ compact = false }: { compact?: boolean }) {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [standalone, setStandalone] = useState(() => isStandaloneMode());

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    const handleUpdate = () => setUpdateAvailable(true);
    const handleInstalled = () => {
      setStandalone(true);
      setInstallPrompt(null);
    };
    const handleInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener(PWA_UPDATE_EVENT, handleUpdate);
    window.addEventListener("appinstalled", handleInstalled);
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener(PWA_UPDATE_EVENT, handleUpdate);
      window.removeEventListener("appinstalled", handleInstalled);
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
    };
  }, []);

  const canOfferIosInstructions = useMemo(
    () => !standalone && isIosDevice(),
    [standalone],
  );

  async function install() {
    if (installPrompt) {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === "accepted") setInstallPrompt(null);
      return;
    }

    if (canOfferIosInstructions) setInstructionsOpen(true);
  }

  if (!online) {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border border-warning/30 bg-warning/10 text-warning-foreground",
          compact ? "h-9 px-2.5 text-xs" : "h-10 px-3 text-sm",
        )}
        title="Sem conexão. Dados clínicos remotos não serão atualizados até a internet voltar."
      >
        <WifiOff className="h-4 w-4" />
        {!compact ? <span>Offline</span> : null}
      </div>
    );
  }

  if (updateAvailable) {
    return (
      <Button
        type="button"
        variant="outline"
        size={compact ? "icon" : "sm"}
        className={compact ? "h-9 w-9" : "gap-2"}
        onClick={() => void activateWaitingServiceWorker()}
        aria-label="Atualizar ClinicalOS"
      >
        <RefreshCw className="h-4 w-4" />
        {!compact ? "Atualizar" : null}
      </Button>
    );
  }

  const showInstall = !standalone && (Boolean(installPrompt) || canOfferIosInstructions);
  if (!showInstall) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size={compact ? "icon" : "sm"}
        className={compact ? "h-9 w-9" : "gap-2"}
        onClick={() => void install()}
        aria-label="Instalar ClinicalOS"
      >
        <Download className="h-4 w-4" />
        {!compact ? "Instalar" : null}
      </Button>

      <Dialog open={instructionsOpen} onOpenChange={setInstructionsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Instalar no iPhone</DialogTitle>
            <DialogDescription>
              O Safari instala o ClinicalOS diretamente na Tela de Início, sem App Store.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3 rounded-xl border bg-muted/30 p-3">
              <Share className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <div className="font-medium">1. Toque em Compartilhar</div>
                <div className="text-muted-foreground">Use o botão de compartilhar na barra do Safari.</div>
              </div>
            </div>
            <div className="rounded-xl border bg-muted/30 p-3">
              <div className="font-medium">2. Escolha “Adicionar à Tela de Início”</div>
              <div className="mt-1 text-muted-foreground">
                Confirme o nome ClinicalOS. O ícone abrirá em modo aplicativo.
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
